import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import useAdGroupDiagnosticsColumns from '../../hooks/useAdGroupDiagnosticsColumns';
import { AdGroupDiagnosticMetric } from '../../types';

import { useGetAdGroupDiagnosticsQuery } from '@/api/endpoints/starvationBidder';
import { SearchField } from '@/features/trafficking';
import { usePlatform } from '@/features/users';
import { Table } from '@/shared/components/table';
import { useGlobalFilterKeys } from '@/shared/hooks';

const AdGroupDiagnostics = () => {
  const platform = usePlatform();
  const isMeta = platform === 'Meta';
  const isDv360 = platform === 'DV360';

  const searchParams = useSearch({
    from: '/_app/algorithm-tools/starvation-bidder',
  });
  const navigate = useNavigate({
    from: '/algorithm-tools/starvation-bidder',
  });

  const {
    diagCampaignOrInsertionId,
    diagCampaignOrInsertionName,
    adGroupPage,
    adGroupPageSize,
    adGroupSearch,
  } = searchParams;

  const { globalFilterKeys, isGlobalFilterReady } = useGlobalFilterKeys();
  const {
    data: adGroupDiagnostics,
    isLoading,
    isFetching,
    isError,
  } = useGetAdGroupDiagnosticsQuery(
    {
      diagCampaignOrInsertionId: diagCampaignOrInsertionId ?? '',
      page: adGroupPage,
      pageSize: adGroupPageSize,
      search: adGroupSearch,
      ...globalFilterKeys,
    },
    { skip: !isGlobalFilterReady || !diagCampaignOrInsertionId }
  );

  const columns = useAdGroupDiagnosticsColumns();
  const table = useReactTable<AdGroupDiagnosticMetric>({
    columns,
    data: adGroupDiagnostics?.results ?? [],
    getCoreRowModel: getCoreRowModel(),

    state: {
      columnPinning: { left: ['nameAndId'] },
      pagination: {
        pageIndex: adGroupPage - 1,
        pageSize: adGroupPageSize,
      },
    },
    enableRowSelection: false,

    manualPagination: true,
    rowCount: adGroupDiagnostics?.count ?? 0,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function'
          ? updater(table.getState().pagination)
          : updater;
      void navigate({
        search: (prev) => ({
          ...prev,
          adGroupPage: newPagination.pageIndex + 1,
          adGroupPageSize: newPagination.pageSize,
        }),
        resetScroll: false,
      });
    },
  });

  const handleSearchChange = (value: string) => {
    const normalizedValue = value || undefined;
    const searchChanged = normalizedValue !== adGroupSearch;

    if (!searchChanged) {
      return;
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        adGroupSearch: normalizedValue,
        adGroupPage: 1,
      }),
      resetScroll: false,
    });
  };

  const handleBack = () => {
    void navigate({
      search: (prev) => ({
        ...prev,
        diagCampaignOrInsertionId: undefined,
        diagCampaignOrInsertionName: undefined,
        adGroupPage: 1,
        adGroupPageSize: 10,
        adGroupSearch: undefined,
      }),
      resetScroll: false,
    });
  };

  let subTableText = 'Ad group level diagnostics for the selected campaign.';
  let tableErrorText = 'No ad group diagnostics available for this campaign.';
  if (isMeta) {
    subTableText = 'Ad set level diagnostics for the selected campaign.';
    tableErrorText = 'No ad set diagnostics available for this campaign.';
  } else if (isDv360) {
    subTableText =
      'Ad group level diagnostics for the selected insertion order.';
    tableErrorText =
      'No ad group diagnostics available for this insertion order.';
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme) => theme.customShadows.sm,
      }}
    >
      <Stack direction='row' px={3} py={2} gap={2} alignItems='center'>
        <IconButton onClick={handleBack} aria-label='Back to campaigns'>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant='h6' component='h2'>
            {diagCampaignOrInsertionName ??
              (isMeta ? 'Ad Set Diagnostics' : 'Ad Group Diagnostics')}
          </Typography>
          <Typography variant='body2' color='text.secondary' pt={0.5}>
            {subTableText}
          </Typography>
        </Box>
      </Stack>

      <Divider />

      <Stack
        px={2}
        py={1.5}
        direction='row'
        gap={2}
        justifyContent='space-between'
        alignItems='center'
      >
        <SearchField
          initialSearchValue={adGroupSearch}
          debounceSearchChangeCallback={handleSearchChange}
          textFieldprops={{
            id: 'search-adgroup-diagnostics-table',
            placeholder: 'Search by name or ID',
          }}
        />
      </Stack>

      <Divider />

      <Table
        table={table}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        tableLabel={
          isMeta ? 'Ad Set Diagnostics Table' : 'Ad Group Diagnostics Table'
        }
        emptyHeading={isMeta ? 'No ad sets to show' : 'No ad groups to show'}
        emptyText={tableErrorText}
        errorText='There was an error loading the ad group diagnostics data. Please try again.'
      />
    </Paper>
  );
};

export default AdGroupDiagnostics;
